import { Command } from 'commander';
import { ServiceTokenManager } from '@whittlelabs/keep';
import type { BrandConfig } from '../branding/types';
import { resolvePaths, resolveProfile } from '../branding/apply';
import { PairingConfigStore } from '../pairing/store';
import { revokePairing, listPairings } from '../pairing/pairings';

/**
 * `<brand> revoke <name>` - revoke a paired machine by name or id.
 *
 * Refuses to revoke the currently-paired identity to avoid orphaning the
 * local install; use `<brand> init` on the other machine, then revoke
 * from there.
 */
export function buildRevokeCommand(brand: BrandConfig): Command {
  return new Command('revoke')
    .description(`Revoke a paired ${brand.product.title}`)
    .argument('<nameOrId>', 'Machine name or service-identity id to revoke')
    .option('-p, --profile <name>', 'Config profile whose pairing credentials to use (default "default")')
    .action(async (nameOrId: string, options: { profile?: string }) => {
      try {
        const paths = resolvePaths(brand, resolveProfile(brand, options.profile));
        const store = new PairingConfigStore(paths.configFile);
        const pairing = store.read();

        const tokens = new ServiceTokenManager({
          keepApiUrl: pairing.keepApiUrl,
          clientId: pairing.credentials.clientId,
          clientSecret: pairing.credentials.clientSecret,
        });
        const accessToken = await tokens.getToken();
        if (!accessToken) {
          throw new Error('Failed to mint service token from stored credentials.');
        }

        const machines = await listPairings({ brand, accessToken });
        const target = machines.find(
          (m) => m.id === nameOrId || m.name === nameOrId || m.displayName === nameOrId,
        );
        if (!target) {
          throw new Error(`No paired machine matches "${nameOrId}".`);
        }
        if (target.id === pairing.serviceIdentity.id) {
          throw new Error(
            'Refusing to revoke the currently-paired identity. Run `init` on a different machine and revoke from there.',
          );
        }
        await revokePairing({ brand, accessToken, identityId: target.id });
        console.log(`Revoked ${target.name} (${target.id}).`);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        console.error(`revoke failed: ${message}`);
        process.exit(1);
      }
    });
}
